import React, { useMemo, useState } from "react";
import { Table, Tabs, Button, message } from "antd";
import { StarFilled, DownloadOutlined } from "@ant-design/icons";
import { AlertTriangle } from "lucide-react";
import { SetupState, splitLabel } from "./mockData";
import {
  DesignResult,
  DesignResultRow,
  JobRunProgress,
  buildMarketLevelWorkbookBlob,
  describePhase,
  downloadBlob,
  uploadMarketLevelWorkbook,
} from "./rctExperimentApi";
import LineChartWithTitle from "../../../components/LineChartWithTitle";
import RctLoadingIndicator from "./RctLoadingIndicator";

interface ResultsStepProps {
  jobConfigId: string;
  setup: SetupState;
  designResult: DesignResult | null;
  progress: JobRunProgress | null;
  runError: string | null;
}

const currency = (n: number) => `$${Math.round(n).toLocaleString()}`;
// Caps any decimal metric at 2 places (RMSPE, MDE %, etc. were a mix
// of .toFixed(1)/.toFixed(3)/unformatted); whole numbers stay whole since
// maximumFractionDigits doesn't pad trailing zeros.
const formatNumber = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

interface MarketLevelRow {
  name: string;
  type: "Treatment" | "Control";
  totalSalesCount: number;
  percentageOfTreatment: number; // 0-100; always 0 for Control
}

// Mirrors the data scientist's original generateSummaryCSV(): total sales
// per location (full history) and each location's share of total treatment
// -group sales - control locations always show 0% since the share is
// defined relative to the treatment group only.
const buildMarketLevelRows = (winner: DesignResultRow, design: DesignResult): MarketLevelRow[] => {
  const preview = design.splitPreview[winner.splitKey];
  if (!preview) return [];
  const totalSales = design.locationTotalSales || {};

  const treatmentTotal = preview.treatmentLocations.reduce(
    (sum, loc) => sum + (totalSales[loc] || 0),
    0
  );

  const treatment: MarketLevelRow[] = preview.treatmentLocations.map((name) => {
    const sales = totalSales[name] || 0;
    return {
      name,
      type: "Treatment",
      totalSalesCount: sales,
      percentageOfTreatment: treatmentTotal > 0 ? (sales / treatmentTotal) * 100 : 0,
    };
  });
  const control: MarketLevelRow[] = preview.controlLocations.map((name) => ({
    name,
    type: "Control",
    totalSalesCount: totalSales[name] || 0,
    percentageOfTreatment: 0,
  }));

  return [...treatment, ...control].sort((a, b) => a.name.localeCompare(b.name));
};

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-5 ${className}`}>{children}</div>
);

const CardHeader: React.FC<{ title: string; tag?: string }> = ({ title, tag }) => (
  <div className="flex justify-between items-center mb-4">
    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    {tag && (
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
        {tag}
      </span>
    )}
  </div>
);

const StatCard: React.FC<{ label: string; value: string; sub?: string; winner?: boolean }> = ({
  label,
  value,
  sub,
  winner,
}) => (
  <div
    className={`border rounded-xl p-4 shadow-sm ${
      winner ? "border-warning-500 bg-gradient-to-b from-warning-50 to-white" : "border-gray-200 bg-white"
    }`}
  >
    <div className="text-xs uppercase tracking-wide font-semibold text-gray-500">{label}</div>
    <div className={`font-mono text-xl font-semibold mt-2 ${winner ? "text-primary" : "text-gray-900"}`}>
      {value}
    </div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

// Full detail for ONE selected split: its own best duration ("winner" scoped
// to this split, not the global best), every duration trial run for it, its
// market-level assignment, and its investment calculation breakdown.
const SplitDetailPanel: React.FC<{
  jobConfigId: string;
  splitKey: string;
  splitTrials: DesignResultRow[];
  winner: DesignResultRow;
  designResult: DesignResult;
  setup: SetupState;
}> = ({ jobConfigId, splitKey, splitTrials, winner, designResult, setup }) => {
  const marketRows = useMemo(
    () => buildMarketLevelRows(winner, designResult),
    [winner, designResult]
  );
  const [downloading, setDownloading] = useState(false);

  const handleDownloadMarketLevel = () => {
    setDownloading(true);
    try {
      const blob = buildMarketLevelWorkbookBlob(marketRows, `${splitKey} Markets`);
      downloadBlob(blob, `market_level_investments_${splitKey}.xlsx`);
      // Archival copy in S3 - fire and forget, doesn't block or fail the download.
      uploadMarketLevelWorkbook(jobConfigId, splitKey, blob).catch((err) => {
        console.error("[RctExperiment] Failed to archive market-level workbook to S3:", err);
      });
    } catch (err) {
      console.error("[RctExperiment] Failed to build market-level workbook:", err);
      message.error("Failed to generate the Excel file.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-4 gap-3.5 mb-5">
        <StatCard
          label="Best Duration"
          value={`${winner.duration} wks`}
          sub="Lowest normalized average error for this split"
          winner
        />
        <StatCard
          label="Test / Control DMAs"
          value={`${winner.testMarkets} / ${winner.controlMarkets}`}
          sub={`${splitLabel(splitKey)} split`}
        />
        <StatCard
          label="Investment / Week"
          value={currency(winner.incrementalInvestment / winner.duration)}
          sub="At this split"
        />
        <StatCard label="MDE %" value={`${formatNumber(winner.percentageLift)}%`} sub="On predicted test KPI" />
      </div>

      <Card>
        <CardHeader title="Winning Trial Detail" tag="Best of the combined search across your candidate durations" />
        <Table
          size="small"
          pagination={false}
          rowKey={(r) => `${r.splitKey}-${r.duration}`}
          dataSource={splitTrials}
          rowClassName={(r) => (r === winner ? "!bg-warning-50" : "")}
          columns={[
            { title: "Duration", key: "duration", render: (_, r) => `${r.duration} wks` },
            { title: "RMSPE", key: "rmspe", render: (_, r) => formatNumber(r.validationRMSPE) },
            { title: "Resid. Std Dev", key: "residStdDev", render: (_, r) => formatNumber(r.validationSD) },
            { title: "Resid. Bias", key: "residBias", render: (_, r) => formatNumber(r.validationBias) },
            {
              title: "Normalized Average Error",
              key: "normalizedAverageError",
              // Older stored results (run before this field existed) won't
              // have it - fall back rather than crash on undefined.toLocaleString().
              render: (_, r) =>
                r.normalizedAverageError !== undefined ? formatNumber(r.normalizedAverageError) : "N/A",
            },
            {
              title: "Invest / Wk",
              key: "incrementalInvestmentPerWeek",
              render: (_, r) => currency(r.incrementalInvestment / r.duration),
            },
          ]}
        />
        <div className="mt-3 text-xs text-gray-400">
          Picked by fit quality among trials that already met your Weekly Investment Cap. Normalized Average
          Error combines RMSPE + Std Dev + Bias (lower score wins) across every trial actually tried for this
          split.
        </div>
      </Card>

      <Card>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Market Level Investments</h3>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full inline-block mt-2">
              {marketRows.length} markets · {winner.duration} wk duration
            </span>
          </div>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadMarketLevel}
            loading={downloading}
          >
            Download
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Treatment/control assignment for this split, from the market pairing computed on your uploaded
          data.
        </p>
        <Table
          size="small"
          pagination={{ pageSize: 8, size: "small" }}
          rowKey="name"
          dataSource={marketRows}
          columns={[
            { title: "Market", dataIndex: "name", key: "name" },
            {
              title: "Type",
              key: "type",
              render: (_, r) => (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    r.type === "Treatment" ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {r.type}
                </span>
              ),
            },
            {
              title: "Total Sales Count",
              key: "totalSalesCount",
              sorter: (a, b) => a.totalSalesCount - b.totalSalesCount,
              render: (_, r) => <span className="font-mono">{Math.round(r.totalSalesCount).toLocaleString()}</span>,
            },
            {
              title: "Percentage Of Treatment",
              key: "percentageOfTreatment",
              sorter: (a, b) => a.percentageOfTreatment - b.percentageOfTreatment,
              render: (_, r) =>
                r.type === "Treatment" ? (
                  <span className="font-mono">{formatNumber(r.percentageOfTreatment)}%</span>
                ) : (
                  <span className="font-mono text-gray-400">0%</span>
                ),
            },
          ]}
        />
      </Card>

      <Card>
        <CardHeader
          title="Actual vs. Predicted — Test Market Average"
          tag={`Best trial · ${winner.duration}-week test`}
        />
        {winner.actualKPI?.length > 0 ? (
          <LineChartWithTitle
            title=""
            labels={winner.actualDates}
            xAxisTitle="Week"
            yAxisTitle="Avg KPI"
            height="320px"
            dataSets={[
              {
                name: "Actual test average",
                data: winner.actualKPI,
                borderColor: "#0055B8",
                backgroundColor: "#0055B8",
                textColor: "#0055B8",
              },
              {
                name: "Predicted (synthetic control)",
                data: winner.predictedKPI,
                borderColor: "#9CA3AF",
                backgroundColor: "#9CA3AF",
                textColor: "#9CA3AF",
                lineType: "dashed",
              },
            ]}
          />
        ) : (
          <p className="text-sm text-gray-500">No post-campaign window data available for this trial.</p>
        )}
      </Card>

      <Card>
        <CardHeader title="Investment Calculation" tag={`Best duration · ${winner.duration} wks`} />
        <div className="flex flex-col">
          {[
            {
              // guessEffectSize from rct_experiment.py's runDsExactSplitSearch -
              // backend only returns selectedEffectSize (guessEffectSize - 1),
              // so this reverses that to show the DS script's original guess.
              name: "Effect Size",
              result: formatNumber(winner.selectedEffectSize + 1),
            },
            {
              name: "Standard Error",
              result: formatNumber(winner.standardError),
            },
            { name: "Min. Detectable Effect", result: formatNumber(winner.mde) },
            { name: "Percentage Lift", result: `${formatNumber(winner.percentageLift)}%` },
            { name: "Average Baseline KPI", result: formatNumber(winner.averageBaselineKPI) },
            {
              name: "Treatment Locations × Duration",
              result: winner.testMarkets * winner.duration,
            },
            {
              name: "Investment Per Week",
              result: currency(winner.incrementalInvestment / winner.duration),
            }
          ].map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-[220px_1fr_auto] items-center gap-4 py-3 border-b border-gray-100 last:border-b-0"
            >
              <div className="font-semibold text-sm text-gray-900">{row.name}</div>
              <div className="font-mono text-base font-semibold text-primary text-right whitespace-nowrap">
                {row.result}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 p-4 rounded-lg bg-primary flex justify-between items-center text-white">
          <div className="text-xs font-semibold uppercase tracking-wide">Incremental Investment(Overall)</div>
          <div className="font-mono text-2xl font-bold">{currency(winner.incrementalInvestment)}</div>
        </div>
      </Card>
    </div>
  );
};

const ResultsStep: React.FC<ResultsStepProps> = ({
  jobConfigId,
  setup,
  designResult,
  progress,
  runError,
}) => {
  // Hooks must run unconditionally on every render (Rules of Hooks), so this
  // is computed before the loading/error early-returns below, tolerating a
  // null designResult while the design job is still running.
  const trials = useMemo(() => designResult?.results ?? [], [designResult]);

  const { orderedSplitKeys, bestBySplit, trialsBySplit } = useMemo(() => {
    const bySplit = new Map<string, DesignResultRow[]>();
    trials.forEach((t) => {
      const list = bySplit.get(t.splitKey) ?? [];
      list.push(t);
      bySplit.set(t.splitKey, list);
    });

    const best: Record<string, DesignResultRow> = {};
    bySplit.forEach((list, key) => {
      best[key] = list.reduce((a, b) => (a.compositeZScore <= b.compositeZScore ? a : b));
    });

    const ordered = Array.from(bySplit.keys()).sort(
      (a, b) => best[a].compositeZScore - best[b].compositeZScore
    );

    return { orderedSplitKeys: ordered, bestBySplit: best, trialsBySplit: bySplit };
  }, [trials]);

  const [activeSplit, setActiveSplit] = useState<string | undefined>(orderedSplitKeys[0]);
  const currentSplit = activeSplit && bestBySplit[activeSplit] ? activeSplit : orderedSplitKeys[0];

  if (runError) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-6 shadow-sm mb-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-gray-900 mb-1">Experiment design failed</div>
            <p className="text-sm text-gray-600">{runError}</p>
            <p className="text-sm text-gray-500 mt-2">
              Go back to Configure Experiment to adjust your data or parameters and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!designResult) {
    return (
      <RctLoadingIndicator
        title={describePhase(progress?.phase)}
        message={progress?.message}
        percent={progress?.progressPct ?? 0}
      />
    );
  }

  if (orderedSplitKeys.length === 0) {
    return (
      <Card>
        <p className="text-sm text-gray-500">No trial results were returned for this experiment.</p>
      </Card>
    );
  }

  const overallBestKey = orderedSplitKeys[0];

  return (
    <div>
      <div className="flex items-center gap-2 bg-primary/10 text-primary rounded-lg px-3.5 py-2.5 text-sm font-medium mb-5">
        Results scoped to <span className="font-mono font-semibold">{orderedSplitKeys.length}</span> split scenario(s)
        · each searched across <span className="font-mono font-semibold">{setup.durations.length}</span> candidate
        duration(s) together, one winner per split
      </div>

      <Card>
        <CardHeader title="Compare Splits" tag="Each row is that split's own best duration" />
        <Table
          size="small"
          pagination={false}
          rowKey="splitKey"
          dataSource={orderedSplitKeys.map((key) => ({ ...bestBySplit[key], splitKey: key }))}
          rowClassName={(r) => (r.splitKey === currentSplit ? "!bg-primary/5" : "")}
          onRow={(r) => ({ onClick: () => setActiveSplit(r.splitKey), style: { cursor: "pointer" } })}
          columns={[
            {
              title: "Split",
              key: "split",
              render: (_, r) => (
                <span className="flex items-center gap-1.5 font-medium">
                  {r.splitKey === overallBestKey && <StarFilled className="text-warning-500 text-xs" />}
                  {splitLabel(r.splitKey)}
                </span>
              ),
            },
            { title: "Best Duration", key: "duration", render: (_, r) => `${r.duration} wks` },
            { title: "Test / Control", key: "markets", render: (_, r) => `${r.testMarkets} / ${r.controlMarkets}` },
            { title: "MDE %", key: "mde", render: (_, r) => `${formatNumber(r.percentageLift)}%` },
            {
              title: "Invest / Wk",
              key: "incrementalInvestmentPerWeek",
              render: (_, r) => currency(r.incrementalInvestment / r.duration),
            },
          ]}
        />
        <div className="mt-3 text-xs text-gray-400">
          Sorted by each split's own composite error score, ascending ·{" "}
          <StarFilled className="text-warning-500" /> = best overall · click a row to jump to its detail below
        </div>
      </Card>

      <Tabs
        activeKey={currentSplit}
        onChange={setActiveSplit}
        items={orderedSplitKeys.map((key) => ({
          key,
          label: (
            <span className="flex items-center gap-1.5">
              {key === overallBestKey && <StarFilled className="text-warning-500 text-xs" />}
              {splitLabel(key)}
            </span>
          ),
          children: (
            <SplitDetailPanel
              jobConfigId={jobConfigId}
              splitKey={key}
              splitTrials={[...trialsBySplit.get(key)!].sort((a, b) => a.compositeZScore - b.compositeZScore)}
              winner={bestBySplit[key]}
              designResult={designResult}
              setup={setup}
            />
          ),
        }))}
      />
    </div>
  );
};

export default ResultsStep;
